import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import repositoryContainer from '../../../services/RepositoryContainer';
import { useAuth } from '../../../components/context/AuthContext';

const LogicSchedulingEngine = () => {
  // Use setUser from AuthContext for global state persistence
  const { user, setUser } = useAuth();

  const [formData, setFormData] = useState({
    slotHoldingTime: 0,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync with Auth Context on load/refresh
  useEffect(() => {
    const company = user?.company;
    if (company) {
      setFormData({
        // Map the backend value to our state
        slotHoldingTime: company.slotHoldingTime || 0,
      });
      setHasChanges(false);
    }
  }, [user]);

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    // Ensure we send a clean integer to the backend
    const numValue = parseInt(value, 10);
    setFormData((prev) => ({
      ...prev,
      [name]: isNaN(numValue) ? 0 : numValue,
    }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      setMessage(null);

      // Backend expects exactly: { "slotHoldingTime": number }
      const payload = {
        slotHoldingTime: formData.slotHoldingTime,
      };

      const updatedResponse = await repositoryContainer.company.updateBookingSettings(payload);
      
      // Handle potential nested response shapes
      const newSettings = updatedResponse.bookingSettings || updatedResponse;

      // Update Global Auth Context
      if (typeof setUser === 'function') {
        setUser((prev) => ({
          ...prev,
          company: {
            ...prev.company,
            ...newSettings,
          },
        }));
      }

      setHasChanges(false);
      setMessage({ type: 'success', text: 'Scheduling engine updated successfully!' });
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to update scheduling engine',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const company = user?.company;
    if (company) {
      setFormData({
        slotHoldingTime: company.slotHoldingTime || 0,
      });
    }
    setHasChanges(false);
    setMessage(null);
  };

  return (
    <div className="settings-bento-card">
      <div className="settings-card-header">
        <h3 className="settings-card-title">Booking Rules</h3>
        <p className="settings-card-description">Control how customers interact with your calendar.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-bold block mb-2">Slot Holding Time (Minutes)</label>
          <p className="text-xs text-muted-foreground mb-3">
            How long a time slot stays "reserved" while a client finishes their booking.
          </p>
          <Input
            name="slotHoldingTime" // Match the state and backend key
            type="number"
            min="0"
            value={formData.slotHoldingTime}
            onChange={handleNumberChange}
            placeholder="e.g. 15"
            className="max-w-[200px]"
          />
        </div>
      </div>

      {message && (
        <div className={`text-sm mt-4 p-3 rounded-md border animate-in fade-in slide-in-from-top-1 ${
          message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-destructive'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex gap-3 mt-6">
        {hasChanges && (
          <Button variant="ghost" onClick={handleCancel} disabled={loading} className="flex-1">
            Discard
          </Button>
        )}
        <Button 
          variant="default" 
          className="flex-1" 
          onClick={handleSubmit} 
          disabled={loading || !hasChanges}
        >
          {loading ? 'Saving...' : 'Save Engine Settings'}
        </Button>
      </div>
    </div>
  );
};

export default LogicSchedulingEngine;